// libs
import { skipToken } from '@apollo/client/react'
import React from 'react'

// styles
import {
  ProjectMembershipSortingKeys,
  ProjectPreviewFragmentFragment,
  ProjectType,
  SortDirection,
  useProjectMemberships
} from '@lopo-tech/lopo-api'
import { MyPublicParticipationsIcon } from 'src/components/icons/projects/public-participation/my-public-participations'
import { PublicParticipationIcon } from 'src/components/icons/projects/public-participation/public-participation'
import {
  PROJECT_LIST_PARTIAL,
  PROJECT_MY_LIST_PARTIAL, getProjectRoutes
} from 'src/constants/urls'
import { textResources } from 'src/lang/de'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { useSelectCurrentProfile } from 'src/reducers/user/user_hooks'
import { COLOR_TYPE_PRIMARY, COLOR_TYPE_PRIMARY_2 } from 'src/utils/color'
import { InfrastructureProjectListSection } from '../infrastructure_project_list_section/infrastructure_project_list_section'
import { ProjectListSection } from '../project_list_section/project_list_section'
import { ProjectListWrapper } from '../project_list_wrapper/project_list_wrapper'
import { hasMoreThanShown } from './has_more_than_shown'

const t = textResources.projects

type Props = {
  projectType: ProjectType;
  projects: ProjectPreviewFragmentFragment[] | undefined;
  totalCount: number;
  loading: boolean;
  isError: boolean;
  refetch: () => void;
}

// project types for which to show own projects section
const OWN_PROJECT_TYPES: ProjectType[] = [ProjectType.PublicParticipationProject]

const ProjectsOverview: React.FC<Props> = ({ projectType, projects, totalCount, loading, isError, refetch }) => {
  const locationShape = useGetAppLocationShape()
  const routes = getProjectRoutes(projectType)

  const currentProfile = useSelectCurrentProfile()

  const projectMemberships = useProjectMemberships(
    currentProfile?.id && OWN_PROJECT_TYPES.includes(projectType)
      ? {
        variables: {
          memberId: currentProfile.id,
          page: 1,
          perPage: 5,
          sorting: {
            direction: SortDirection.Desc,
            fieldName: ProjectMembershipSortingKeys.ProjectLastActivityAt
          }
        }
      }
      : skipToken
  )

  const ownProjects =
    projectMemberships.data?.projectMemberships?.items.map(
      (membership) => membership.project
    )?.filter((project) => project.type === projectType) || []
  const ownProjectsTotalCount = projectMemberships.data?.projectMemberships?.pageInfo.totalCount

  const showErrorState = !loading && isError
  const showEmptyState =
    !loading &&
    !isError &&
    totalCount === 0 &&
    ownProjects.length === 0 &&
    !projectMemberships.loading &&
    !projectMemberships.error

  return (
    <ProjectListWrapper
      showErrorState={showErrorState}
      showEmptyState={showEmptyState}
      refetch={refetch}
      projectType={projectType}
    >
      {!!ownProjects?.length && (
        <ProjectListSection
          title={t.overview.sectionMyProjects}
          count={ownProjectsTotalCount}
          icon={
            <MyPublicParticipationsIcon
              size={24}
              primaryColor={COLOR_TYPE_PRIMARY}
              secondaryColor={COLOR_TYPE_PRIMARY_2}
            />
          }
          projects={ownProjects}
          viewAllHref={
            hasMoreThanShown(ownProjectsTotalCount, ownProjects.length)
              ? `/${locationShape?.slug}${routes.base}${PROJECT_MY_LIST_PARTIAL}`
              : undefined
          }
        />
      )}
      {!!projects?.length && (
        projectType === ProjectType.InfrastructureProject
          ? (
            <InfrastructureProjectListSection
              title={t.overview.sectionAllProjects}
              count={totalCount}
              icon={
                <PublicParticipationIcon
                  size={24}
                  primaryColor={COLOR_TYPE_PRIMARY}
                  secondaryColor={COLOR_TYPE_PRIMARY_2}
                />
              }
              projects={projects}
              viewAllHref={
                hasMoreThanShown(totalCount, projects.length)
                  ? `/${locationShape?.slug}${routes.base}${PROJECT_LIST_PARTIAL}`
                  : undefined
              }
            />
          )
          : (
            <ProjectListSection
              title={t.overview.sectionAllProjects}
              count={totalCount}
              icon={
                <PublicParticipationIcon
                  size={24}
                  primaryColor={COLOR_TYPE_PRIMARY}
                  secondaryColor={COLOR_TYPE_PRIMARY_2}
                />
              }
              projects={projects}
              viewAllHref={
                hasMoreThanShown(totalCount, projects.length)
                  ? `/${locationShape?.slug}${routes.base}${PROJECT_LIST_PARTIAL}`
                  : undefined
              }
            />
          )
      )}
    </ProjectListWrapper>
  )
}

export default ProjectsOverview
